import { Box, Link, Text } from '@shopify/polaris';
import { TableShell } from '../efficacy/TableShell';
import {
  TABLE_CELL_PADDING,
  TABLE_DANGER,
  TABLE_POSITIVE,
  TABLE_SUBTLE,
} from '../efficacy/tableStyles';
import { formatMoney, formatPct, formatUnits } from '../efficacy/formatters';

/**
 * Tabla de series para el informe de Reposición. Versión mucho más
 * ligera que la de Eficacia porque las dimensiones son distintas
 * (no hay in/out season, sí hay comparativa con período anterior).
 *
 * Columnas:
 *   1. Serie (sticky a la izquierda).
 *   2. Compra período (uds + valor).
 *   3. Venta período (uds + valor) — con delta % vs anterior.
 *   4. Stock actual (uds + valor CI).
 *   5. Rotación (anualizada).
 *   6. Cobertura (días).
 *   7. Rentabilidad %.
 */
const COLS = [
  { id: 'family', label: 'Serie', align: 'left', sticky: true, width: 180 },
  { id: 'buy', label: 'Compra (período)', align: 'right', width: 130 },
  { id: 'sell', label: 'Venta (período)', align: 'right', width: 150, sortBy: 'sellUnits' },
  { id: 'stock', label: 'Stock actual', align: 'right', width: 130, sortBy: 'remainUnits' },
  { id: 'rotation', label: 'Rotación', align: 'right', width: 90, sortBy: 'rotation' },
  { id: 'coverage', label: 'Cobertura', align: 'right', width: 100, sortBy: 'coverage' },
  { id: 'profitability', label: 'Rentab.', align: 'right', width: 90, sortBy: 'profitability' },
];

const deltaPct = (cur, prev) => {
  if (prev === 0) return cur > 0 ? null : 0;
  return (cur - prev) / prev;
};

const DeltaText = ({ value }) => {
  if (value == null) return null;
  const arrow = value >= 0 ? '↑' : '↓';
  const color = value >= 0 ? TABLE_POSITIVE : TABLE_DANGER;
  return (
    <span style={{ color, fontSize: '11px' }}>
      {arrow} {formatPct(Math.abs(value))}
    </span>
  );
};

const Cell = ({ children, align, width, sticky, isHeader }) => {
  const Tag = isHeader ? 'th' : 'td';
  return (
    <Tag
      className={sticky ? 'efftable-sticky' : undefined}
      style={{
        textAlign: align,
        padding: TABLE_CELL_PADDING,
        verticalAlign: 'middle',
        width,
        minWidth: width,
        maxWidth: width,
        position: sticky ? 'sticky' : undefined,
        left: sticky ? 0 : undefined,
        zIndex: sticky ? 1 : undefined,
        fontWeight: isHeader ? 600 : 400,
        fontSize: isHeader ? '12px' : '13px',
        color: isHeader ? TABLE_SUBTLE : undefined,
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Tag>
  );
};

const renderCell = (row, col, currency) => {
  if (col.id === 'family') {
    return (
      <Box>
        {row.productId ? (
          <Link url={`/products/${row.productId}`} target="_blank" external monochrome plain>
            <span className="efftable-link">{row.family}</span>
          </Link>
        ) : (
          row.family
        )}
        <div style={{ fontSize: '11px', color: TABLE_SUBTLE }}>
          {[row.gender, row.category, row.subcategory].filter(Boolean).join(' · ')}
        </div>
      </Box>
    );
  }
  if (col.id === 'buy') {
    return (
      <Box>
        {formatUnits(row.buyUnits)}
        <div style={{ fontSize: '11px', color: TABLE_SUBTLE }}>{formatMoney(row.buyValue, currency)}</div>
      </Box>
    );
  }
  if (col.id === 'sell') {
    const dUnits = deltaPct(row.sellUnits, row.prevSellUnits);
    return (
      <Box>
        {formatUnits(row.sellUnits)}
        <div style={{ fontSize: '11px', color: TABLE_SUBTLE }}>
          {formatMoney(row.sellValuePvf, currency)}
        </div>
        <DeltaText value={dUnits} />
      </Box>
    );
  }
  if (col.id === 'stock') {
    return (
      <Box>
        {formatUnits(row.remainUnits)}
        <div style={{ fontSize: '11px', color: TABLE_SUBTLE }}>{formatMoney(row.remainValueCI, currency)}</div>
      </Box>
    );
  }
  if (col.id === 'rotation') return row.rotation != null ? `${row.rotation.toFixed(2)}×` : '—';
  if (col.id === 'coverage') return row.coverage != null ? `${Math.round(row.coverage)} días` : '—';
  if (col.id === 'profitability') {
    if (row.profitability == null) return '—';
    const color = row.profitability < 0 ? TABLE_DANGER : TABLE_POSITIVE;
    return <span style={{ color }}>{formatPct(row.profitability)}</span>;
  }
  return null;
};

export const SeriesTable = ({ rows, currency, sortBy, sortDir, onSort }) => {
  return (
    <TableShell>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {COLS.map((c) => (
                <Cell key={c.id} align={c.align} width={c.width} sticky={c.sticky} isHeader>
                  <span
                    onClick={() => c.sortBy && onSort?.(c.sortBy)}
                    style={{ cursor: c.sortBy ? 'pointer' : 'default' }}
                  >
                    {c.label}
                    {c.sortBy === sortBy && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                  </span>
                </Cell>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.family} className="efftable-row">
                {COLS.map((c) => (
                  <Cell key={c.id} align={c.align} width={c.width} sticky={c.sticky}>
                    {renderCell(r, c, currency)}
                  </Cell>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <Box padding="4">
          <Text variant="bodyMd" tone="subdued" alignment="center" as="p">
            Sin series que mostrar para este período / filtros.
          </Text>
        </Box>
      )}
    </TableShell>
  );
};
